import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';

import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { DropdownComponent } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import {
  FilterService,
  FilterKind,
  NumericFilterDisplayValue,
} from 'app/modules/collection/services/filter.service';
import { ComparisonFilterSet } from 'app/providers/bridge-api/dashboard.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { FilterHandler } from '../comparison-filter-item.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-comparison-filter-numeric-item',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './comparison-filter-numeric-item.component.html',
  styleUrls: ['./comparison-filter-numeric-item.component.scss'],
})
export class ComparisonFilterNumericItemComponent
  implements FilterHandler<NumericFilterDisplayValue, string>, OnChanges, OnInit, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() field: string;
  @Input() filterKind: FilterKind;
  @Input() filters;
  @Input() isEnabled;
  @Input() isReadOnly;

  @Output() filtersChange = new EventEmitter();

  @ViewChildren('operatorDropdown') operatorDropdowns: QueryList<DropdownComponent>;

  displayValue: NumericFilterDisplayValue;
  arithmeticOperators = [
    { id: '>', title: '' },
    { id: '<', title: '' },
    { id: '>=', title: '' },
    { id: '<=', title: '' },
    { id: '=', title: '' },
    { id: '!=', title: '' },
  ];
  keepDisplayValue;

  translationSubscription: Subscription;

  constructor(
    private filterService: FilterService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.translationSubscription = this.translocoService
      .selectTranslateObject('comparison-filter-item.numeric-item')
      .subscribe((o) => {
        this.arithmeticOperators = [
          { id: '>', title: o['greater'] },
          { id: '<', title: o['lesser'] },
          { id: '>=', title: o['greater-equal'] },
          { id: '<=', title: o['lesser-equal'] },
          { id: '=', title: o['equal'] },
          { id: '!=', title: o['not-equal'] },
        ];
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filters) {
      this.displayValue = this.fromFilter(changes.filters.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.translationSubscription?.unsubscribe();
  }

  fromFilter = (filter: ComparisonFilterSet) => {
    if (this.keepDisplayValue) {
      this.keepDisplayValue = false;

      return this.displayValue;
    }

    if (!this.hasFilterValue(filter)) {
      // operator must be undefined if !value null throws kitchen error, see DASH-8854
      return {
        leftOperand: {
          operator: undefined,
          value: '',
        },
        rightOperand: {
          operator: undefined,
          value: '',
        },
      };
    }

    return this.filterService.numericRangeFilterToModel(filter.and[0].value);
  };

  toFilter = () => {
    if (!this.isExpressionValid) {
      return {
        and: [],
        or: [],
        not: [],
      };
    }

    return {
      and: [
        {
          analyticItem: this.field,
          value: this.filterService.numericRangeModelToFilter(this.displayValue),
          kind: this.filterKind,
        },
      ],
      or: [],
      not: [],
    };
  };

  hasFilterValue(filterSet: ComparisonFilterSet) {
    return !!filterSet?.and.length;
  }

  onFilterValueSet() {
    if (!this.isEnabled || this.isReadOnly) {
      return;
    }

    this.keepDisplayValue = true;

    this.filtersChange.emit(this.toFilter());
  }

  onOperatorSet($event, operand) {
    operand.operator = $event.detail.selected.id;

    this.onFilterValueSet();
  }

  onValueSet() {
    this.onFilterValueSet();
  }

  get isBooleanOperatorEnabled() {
    return (
      this.isEnabled &&
      this.displayValue.leftOperand?.operator &&
      this.displayValue.leftOperand?.value !== ''
    );
  }

  get isExpressionValid() {
    if (this.displayValue.leftOperand.operator && this.displayValue.leftOperand.value !== '') {
      if (!this.displayValue?.booleanOperator) {
        return true;
      }

      if (this.displayValue.rightOperand.operator && this.displayValue.rightOperand.value !== '') {
        return true;
      }
    }
  }
}
